import Dexie, { Table } from 'dexie';
import vehicleSectionsApiSlice, { SyncListerFormResponse } from 'services/api/vehicle-sections';
import uploadVehicleSlice from 'services/api/upload-vehicle';
import { store } from 'store/configureStore';
import { generateRandomId } from 'utils/crypto';
import { EventEmitter } from 'eventemitter3';
import { setVideoUploadStatus, UploadStatus } from 'services/listerUploadVehicle/v2';

// --- Types ---
export type SectionNotes = {
  message: string;
  id: number;
  created_at: string;
  updated_at: string;
  author_id: number;
  last_editor_id: number;
  last_edited_from: string;
}; // adapt to your shape
export type SectionImage = { id: string; file?: File }; // file optional if synced
export type SectionCheckbox = { id: string; checked: boolean };

export interface SectionCache {
  id: number;
  data: string;
}

export interface Form {
  id: number; // vehicleId
}

export interface SectionRow {
  id: number;
  formId: number;
  parentId: number | null; // null = root section
  name: string;
  label: string;
  notes: SectionNotes | null;
  grade: string | null;
  audioUrl: string | null;
  order: number | null;
  buyerOrder: number | null;
  synced: boolean;
}

export interface ImageRow {
  id: string;
  serverId: number | null;
  formId: number;
  sectionId: number;
  o: number;
  blob?: Blob;
  expiration_date?: string;
  is_damaged?: boolean;
  url?: string;
  url_thumb?: string;
  notes: SectionNotes | null;
  synced: boolean;
}

export interface CheckboxRow {
  id: number;
  formId: number;
  sectionId: number;
  selected: boolean;
  label: string;
  notes: SectionNotes | null;
  order: number;
  synced: boolean;
}

type EntityType = 'checkbox' | 'section' | 'image' | 'new_image' | 'new_checkbox';

export interface LocalEntityUpdates {
  id: string;
  entityId: number | string;
  entityType: EntityType;
  formId: number;
  key: string;
  value: any;
  timestamp: number;
  synced: boolean;
}

export interface LocalVideoUploads {
  id: string;
  sectionId: number;
  formId: number;
  value: Blob[];
  timestamp: number;
  status: string;
  failed: boolean;
  synced: boolean;
}

export interface LocalRemovedImages {
  id: string;
  imageId: number;
  synced: boolean;
  formId: number;
  timestamp: number;
}

export interface LocalVehicleUpdates {
  id: string;
  formId: number;
  key: string;
  value: any;
  timestamp: number;
  synced: boolean;
}

// --- Dexie Database ---
export class AppDB extends Dexie {
  forms!: Table<Form>;
  sectionsCache!: Table<SectionCache>;
  sections!: Table<SectionRow>;
  images!: Table<ImageRow>;
  checkboxes!: Table<CheckboxRow>;
  entityUpdates!: Table<LocalEntityUpdates>;
  removedImages!: Table<LocalRemovedImages>;
  videoUploads!: Table<LocalVideoUploads>;
  vehicleUpdates!: Table<LocalVehicleUpdates>;

  constructor() {
    super('AppDB');
    this.version(1).stores({
      forms: 'id, updatedAt, synced',
      sections: 'id, synced, [formId+parentId]',
      images: 'id, formId, sectionId, synced, serverId',
      checkboxes: 'id, formId, sectionId, synced',
      entityUpdates: 'id, entityId, entityType, formId, key, value, timestamp, synced',
      removedImages: 'id, formId, imageId, synced, timestamp',
      vehicleUpdates: 'id, formId, key, value, timestamp, synced',
      sectionsCache: 'id, data',
      videoUploads: 'id, sectionId, formId, timestamp, synced'
    });
  }
}

export const db = new AppDB();

export class SyncManager {
  private static instanceMap: Map<number, SyncManager> = new Map();
  private constructor(private vehicleId: number) {}

  private emitter = new EventEmitter();

  private debounceTimeout = 1300;
  private syncTimeout: number | null = null;
  public pendingSync = false;

  public isSyncing = false;
  public isRunning = false;
  public isStopped = false;

  public isPaused = false;

  onSyncFinished?: (syncResponse: SyncListerFormResponse) => void | Promise<void>;

  private emitChange() {
    this.emitter.emit('stateChange');
  }

  get isBusy() {
    return this.isSyncing || this.pendingSync || this.syncTimeout !== null;
  }

  on(event: 'stateChange', listener: () => void) {
    this.emitter.on(event, listener);
  }

  off(event: 'stateChange', listener: () => void) {
    this.emitter.off(event, listener);
  }

  pause() {
    this.isPaused = true;
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }

    this.syncTimeout = null;
    this.pendingSync = false;
    this.emitChange();
  }

  async start() {
    this.isPaused = false;
    this.emitChange();
    await this.syncNow();
  }

  static getInstance(vehicleId: number) {
    console.log(this.instanceMap, 'this.instanceMap');

    if (this.instanceMap.has(vehicleId)) {
      return this.instanceMap.get(vehicleId)!;
    }
    const syncManager = new SyncManager(vehicleId);
    this.instanceMap.set(vehicleId, syncManager);
    return syncManager;
  }

  async getDataForSync() {
    const data = await db.entityUpdates
      .where('formId')
      .equals(this.vehicleId)
      .and(r => !r.synced)
      .sortBy('timestamp');
    const obj: Record<number, any> = {};

    for (const record of data) {
      const key = `${record.entityType}#${record.entityId}`;
      const existing = obj[key] || {};
      obj[key] = { ...existing, [record.key]: record.value };
    }

    const result = Object.entries(obj).map(([key, value]) => {
      const [entityType, entityId] = key.split('#');
      return {
        entityId,
        payload: value,
        entityType
      };
    });

    const sectionUpdates = result.filter(s => !s.entityType.endsWith('image'));
    const imageUpdates = result.filter(s => s.entityType.endsWith('image'));

    const removedImageRecords = await db.removedImages
      .where('formId')
      .equals(this.vehicleId)
      .and(r => !r.synced)
      .toArray();

    const removedImages = removedImageRecords.map(i => i.imageId);

    const vehicleUpdates = await db.vehicleUpdates
      .where('formId')
      .equals(this.vehicleId)
      .and(r => !r.synced)
      .sortBy('timestamp');

    const videoUploads = await db.videoUploads
      .where('formId')
      .equals(this.vehicleId)
      .and(r => !r.synced && r.status === 'unsynced')
      .limit(1)
      .sortBy('timestamp');

    return { sectionUpdates, imageUpdates, removedImages, vehicleUpdates, videoUpload: videoUploads?.[0] };
  }

  scheduleDebouncedSync() {
    console.log(this, 'this obj');
    if (this.isPaused) return;
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }

    console.log('scheduling a sync', this.isSyncing, this.pendingSync);

    this.syncTimeout = setTimeout(() => {
      this.emitChange();
      console.log('trigger from timeout');
      console.log('timeout isSyncing + pendingSync', this.isSyncing, this.pendingSync);
      this.syncNow().catch(console.error);
      this.syncTimeout = null;
    }, this.debounceTimeout);

    this.emitChange();
  }

  stop() {
    this.isRunning = false;
    this.isStopped = true;
    this.onSyncFinished = undefined;
    clearTimeout(this.debounceTimeout);
    SyncManager.instanceMap = new Map();
  }

  async syncVideo(videoUpload: LocalVideoUploads) {
    store.dispatch(setVideoUploadStatus({ vehicleId: this.vehicleId, status: UploadStatus.QUEUED }));

    await store.dispatch(
      uploadVehicleSlice.endpoints.loadSectionVideo.initiate({
        vehicleId: this.vehicleId,
        sectionId: videoUpload.sectionId,
        video: videoUpload.value
      })
    );

    await db.videoUploads
      .where('id')
      .notEqual(videoUpload.id)
      .delete();
  }

  async syncNow() {
    if (this.isPaused) return;
    console.log(this.isSyncing, 'THIS.ISSYNCING');
    if (this.isSyncing) {
      this.pendingSync = true;
      this.emitChange();
      console.log('sync in progress');
      return;
    }
    this.isSyncing = true;

    const { sectionUpdates, imageUpdates, removedImages, vehicleUpdates, videoUpload } = await this.getDataForSync();

    if (videoUpload) {
      console.log('Syncing video upload ', { videoUpload });
      await this.syncVideo(videoUpload);
    }

    if ([sectionUpdates, imageUpdates, removedImages, vehicleUpdates].every(arr => arr.length === 0)) {
      this.isSyncing = false;
      this.emitChange();
      return;
    }

    console.log('Syncing with', { sectionUpdates, imageUpdates, removedImages, vehicleUpdates });

    this.emitChange();
    const syncStart = Date.now();

    try {
      const response = await store
        .dispatch(
          vehicleSectionsApiSlice.endpoints.syncListerForm.initiate({
            vehicleId: this.vehicleId,
            sectionUpdates,
            imageUpdates,
            removedImages,
            vehicleUpdates
          })
        )
        .unwrap();

      if (this.onSyncFinished) {
        await this.onSyncFinished(response);
      }

      await db.entityUpdates
        .where('formId')
        .equals(this.vehicleId)
        .and(item => item.timestamp < syncStart)
        .modify({ synced: true });

      await db.removedImages
        .where('formId')
        .equals(this.vehicleId)
        .and(item => item.timestamp < syncStart)
        .modify({ synced: true });

      await db.vehicleUpdates
        .where('formId')
        .equals(this.vehicleId)
        .and(item => item.timestamp < syncStart)
        .modify({ synced: true });
    } catch (error) {
      console.warn(error);
    } finally {
      this.isSyncing = false;
      if (this.pendingSync) {
        this.pendingSync = false;
        console.log('trigger from microstask queue');
        queueMicrotask(() => this.syncNow());
      }
      this.emitChange();
    }
  }

  async queueEntityUpdate(entityId: string | number, entityType: EntityType, args: KeyValuePair | KeyValuePair[]) {
    console.log(entityId, entityType, args, 'entityId', 'entityType', 'args');
    const wrapped = wrapArray(args);
    const records = wrapped.map(([key, value]) => ({
      id: generateRandomId(),
      entityId,
      entityType,
      formId: this.vehicleId,
      key,
      value,
      timestamp: Date.now(),
      synced: false
    }));

    await db.entityUpdates.bulkAdd(records);
    this.scheduleDebouncedSync();
  }

  async queueVehicleUpdate(args: KeyValuePair | KeyValuePair[]) {
    const wrapped = wrapArray(args);
    const records = wrapped.map(([key, value]) => ({
      id: generateRandomId(),
      formId: this.vehicleId,
      key,
      value,
      timestamp: Date.now(),
      synced: false
    }));

    await db.vehicleUpdates.bulkAdd(records);
    this.scheduleDebouncedSync();
  }

  async queueBulkNewImages(data: BulkAddArgs) {
    const records = data.map(({ entityId, file }) => ({
      id: generateRandomId(),
      entityId,
      entityType: 'new_image' as EntityType,
      formId: this.vehicleId,
      key: 'file',
      value: file,
      timestamp: Date.now(),
      synced: false
    }));

    await db.entityUpdates.bulkAdd(records);
    this.scheduleDebouncedSync();
  }

  async queueRemovedImage(imageId: number) {
    await db.removedImages.add({
      id: generateRandomId(),
      formId: this.vehicleId,
      imageId,
      synced: false,
      timestamp: Date.now()
    });
    this.scheduleDebouncedSync();
  }

  async queueVideoUpload(sectionId: number, video: Blob[]) {
    await db.videoUploads.add({
      id: generateRandomId(),
      formId: this.vehicleId,
      sectionId,
      value: video,
      synced: false,
      timestamp: Date.now(),
      status: 'unsynced',
      failed: false
    });
    this.scheduleDebouncedSync();
  }

  async waitUntilSynced(): Promise<void> {
    console.log(this.isBusy, 'this.isBusy waitUntilSynced');
    if (!this.isBusy) return;

    return new Promise<void>(resolve => {
      const check = () => {
        console.log(this.isBusy, 'this.isBusy check');
        if (!this.isBusy) {
          this.off('stateChange', check);
          resolve();
        }
      };
      this.on('stateChange', check);
    });
  }
}

function wrapArray(input: KeyValuePair | KeyValuePair[]): KeyValuePair[] {
  return Array.isArray(input[0]) ? (input as KeyValuePair[]) : [input as KeyValuePair];
}

export type BulkAddArgs = Array<{ entityId: string; file: ArrayBuffer }>;

type KeyValuePair = [string, string | number | boolean | Blob | Blob[] | ArrayBuffer | null];
